import React from 'react';
import { ForSaleConfiguration, ForSaleData, RegistrationAttributions } from "./LineItemTypes"
import { GACntxt } from '../../../AllianceApp';
import IndividualRegistrationForm, { RadioButtonOptions, radioButtonOptionsReverseMap } from './IndividualRegistrationForm';

/**
 * Implementation for the donation line item type, which saves attribution data for a donation.
 */

/**
 * Props for the donationLineItem
 * lineItemName: Name of line item
 * itemConfiguration: Configuration of the donation line item
 * onDataChange: Callback that is called when the data provided in the form has changed.
 */
interface ForSaleLineItemProps
{
    componentName: string,
    lineItemName: string,
    itemConfiguration: ForSaleConfiguration,
    initialData: ForSaleData,
    onDataChange : (lineItemId: number, data: ForSaleData, isValid: boolean, errorMsg?: string) => void,
    disableInputs ?: boolean,
    quantity : number,
    onQuantityChange : (qty: number) => Promise<boolean>,
    disableIncrBtn ? : boolean,
}


const ForSaleLineItem = (props: ForSaleLineItemProps) => 
{
    const userContext = React.useContext(GACntxt);

    const constructDefaultDataMap = () =>
    {
        let startingMap = {}
        
        for (let i = 1; i <= props.quantity; i++)
        {
            const dataElement = props.initialData?.registrationAttributions ? props.initialData.registrationAttributions.find((att) => att.index === i) : undefined;
            console.log(dataElement);
            startingMap = {...startingMap, [i] : 
            {
                index: i,
                userId: userContext.userTGAId,
                target: dataElement ? dataElement.target : (i === 1 ? 'loggedInUser' : 'custom'),
                customName: dataElement ? dataElement.customName :undefined,
                customEmail: dataElement ? dataElement.customEmail :undefined
            }}
        }

        return startingMap;
    }

    const [dataMap, setDataMap] = React.useState<Record<number, any>>(constructDefaultDataMap());
    const [validMap, setValidMap] = React.useState<Record<number, any>>({});

    React.useEffect(() => 
    {
        //First Delete entries that no longer apply
        Object.keys(dataMap).forEach((key) => {
            const keynum = Number(key);
            if(keynum > props.quantity)
            {
                setDataMap((oldMap) =>
                {
                    const {[keynum] : _, ...newMap} = oldMap;
                    return newMap;
                })
            }
        });

        Object.keys(validMap).forEach((key) => {
            const keynum = Number(key);
            if(keynum > props.quantity)
            {
                setValidMap((oldMap) =>
                {
                    const {[keynum] : _, ...newMap} = oldMap;
                    return newMap;
                })
            }
        });

        //Then add new entries that are required
        for (let i = 1; i <= props.quantity; i++)
        {
            if(!(i in dataMap))
            {
                setDataMap((oldMap) =>
                {
                    return({...oldMap, [i] : 
                    {
                        index: i,
                        userId: userContext.userTGAId,
                        target: undefined,
                        customName: undefined,
                        customEmail: undefined
                    }})
                })
            }

            if(!(i in validMap))
            {
                setValidMap((oldMap) =>
                {
                    const isDataValid = dataMap[i] && dataMap[i].target && dataMap[i].target === 'loggedInUser';
                    return({...oldMap, [i] : isDataValid });
                })
            }
        }

        
    },[props.quantity]);

    React.useEffect(() => 
    {
        const attributions = Object.values(dataMap) as RegistrationAttributions[];
        const validity = Object.values(validMap).every(val => val === true);
        const data = 
        {
            dbId : props.itemConfiguration.dbId,
            registrationAttributions: attributions
        }
        console.log(data);
        props.onDataChange(props.itemConfiguration.dbId, data, validity, validity ? undefined : `Waiting for ${props.lineItemName} assignment`);
    },[dataMap, validMap]);

    const onSingleFormChange = (index: number, data: any, isValid: boolean, errorMsg?: string) =>
    {
        setDataMap((oldMap) => 
        {
            return {...oldMap, [index] : data};
        });

        setValidMap((oldMap) =>
        {
            return {...oldMap, [index] : isValid};
        });
    }

    const isUserSelectedElsewhere = (index: number, fullData: RegistrationAttributions[]): boolean =>
    {
        return fullData.reduce((result, data) => 
        {
            if(data.index == index)
            {
                return result
            }
            else
            {
                return result || (data.target === RadioButtonOptions.loggedInUser)
            }
        }, false);
    }

    const perRegistrationForm = (data: RegistrationAttributions[]) =>
    {
        return data.map((perRegData, index, fullData) =>
        {
            //If perRegData.target is defined, use that as the default value.
            //Otherwise, set the first form to logged in user, and all remaining to custom.
            const defVal = (index === 0) ? RadioButtonOptions.loggedInUser : RadioButtonOptions.custom;

            return(
                <IndividualRegistrationForm
                    loggedInUser = {userContext.userTGAName ?? ""}
                    componentName = {props.componentName.concat(perRegData.index.toString())}
                    regData = {perRegData}
                    isUserTargetEnabled = {!isUserSelectedElsewhere(perRegData.index, fullData)}
                    defaultOption = {perRegData.target ? radioButtonOptionsReverseMap(perRegData.target) : defVal}
                    disableInputs = {props.disableInputs ?? false} 
                    onDataChange = {onSingleFormChange}
                />
            )
        })
    }

    const areQuantityBtnsDisabled = () : boolean[] => 
    {
        if (props.disableInputs)
        {
            return [true, true];
        }

        const maxInv = props.itemConfiguration.maximumPermittedInventory;

        

        // if disableInputs is false, do some checks...
        
        return [(props.quantity < 1) , (props.quantity >= maxInv) || (props.disableIncrBtn??true)];
    }

    const changeQuantity = (adj: number) => 
    {
        if (!props.onQuantityChange(props.quantity + adj)) {
            if (adj > 0) {
                // could adjust upward, disable the Increment btn
                props.disableIncrBtn = false;
            } 
        }

        if ((adj < 0) && (props.disableIncrBtn??true)){
            // re-enable the increment button is the decrement button is used
            props.disableIncrBtn = false;
        }
    }

    return(
        <div>
            <div className='lineItem-qtySelect'>
                <button
                    className='colPanel-button'
                    disabled={areQuantityBtnsDisabled()[0]}
                    onClick={() => {
                        changeQuantity(-1);
                    }}>-</button>
                <b className='colPanel-inline'>{props.quantity}</b>
                <button
                    className='colPanel-button'
                    disabled={areQuantityBtnsDisabled()[1]}
                    onClick={() => {
                        changeQuantity(1);
                    }}>+</button>
            </div>
            <div>
                {perRegistrationForm(Object.values(dataMap))}
            </div>
        </div>
    )

}

export default ForSaleLineItem;